// services/frontend/src/utils/fileExtraction.ts
// Shared utilities for extracting files from drag/drop and file picker
// Supports both individual files and folders (recursive traversal)

import logger from '@/utils/logger';

/**
 * Options for file extraction
 */
export interface FileExtractionOptions {
  /** Maximum depth for folder recursion (default: 10) */
  maxDepth?: number;
  /** Maximum total files to extract (default: 1000) */
  maxFiles?: number;
}

const DEFAULT_OPTIONS: Required<FileExtractionOptions> = {
  maxDepth: 10,
  maxFiles: 1000
};

/**
 * Extract all files from a DataTransfer object (drag/drop)
 * Supports mixed files and folders - folders are recursively traversed
 *
 * @param dataTransfer - The DataTransfer from a drop event
 * @param options - Extraction options
 * @returns Promise resolving to array of File objects
 */
export async function extractFilesFromDataTransfer(
  dataTransfer: DataTransfer,
  options: FileExtractionOptions = {}
): Promise<File[]> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const files: File[] = [];

  // Check if webkitGetAsEntry is supported (it is in all modern browsers)
  const items = dataTransfer.items;
  if (!items || items.length === 0) {
    return [];
  }

  // Check if first item supports webkitGetAsEntry
  const firstItem = items[0];
  if (typeof firstItem.webkitGetAsEntry !== 'function') {
    // Fallback: just use dataTransfer.files (no folder support)
    logger.warn('FileExtraction', 'webkitGetAsEntry not supported, falling back to flat files');
    return Array.from(dataTransfer.files);
  }

  // Collect all entries first (they become invalid after the event handler returns)
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        entries.push(entry);
      }
    }
  }

  // Now process all entries (can be async)
  for (const entry of entries) {
    if (files.length >= opts.maxFiles) {
      logger.warn('FileExtraction', `Reached max files limit (${opts.maxFiles}), stopping extraction`);
      break;
    }

    const extractedFiles = await traverseEntry(entry, 0, opts);
    files.push(...extractedFiles);
  }

  logger.info('FileExtraction', `Extracted ${files.length} files from drop`, {
    itemCount: items.length,
    fileCount: files.length
  });

  return files.slice(0, opts.maxFiles);
}

/**
 * Recursively traverse a FileSystemEntry to extract all files
 *
 * @param entry - The FileSystemEntry to traverse
 * @param depth - Current recursion depth
 * @param options - Extraction options
 * @returns Promise resolving to array of File objects
 */
async function traverseEntry(
  entry: FileSystemEntry,
  depth: number,
  options: Required<FileExtractionOptions>
): Promise<File[]> {
  if (depth > options.maxDepth) {
    logger.warn('FileExtraction', `Max depth (${options.maxDepth}) reached at: ${entry.fullPath}`);
    return [];
  }

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    try {
      const file = await getFileFromEntry(fileEntry);
      return [file];
    } catch (error) {
      logger.error('FileExtraction', `Failed to get file from entry: ${entry.fullPath}`, { error });
      return [];
    }
  }

  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const files: File[] = [];

    try {
      const entries = await readDirectoryEntries(dirEntry);

      for (const childEntry of entries) {
        const childFiles = await traverseEntry(childEntry, depth + 1, options);
        files.push(...childFiles);

        if (files.length >= options.maxFiles) {
          break;
        }
      }
    } catch (error) {
      logger.error('FileExtraction', `Failed to read directory: ${entry.fullPath}`, { error });
    }

    return files;
  }

  return [];
}

/**
 * Get a File object from a FileSystemFileEntry
 */
function getFileFromEntry(fileEntry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    fileEntry.file(resolve, reject);
  });
}

/**
 * Read all entries from a directory
 * Note: readEntries() may not return all entries in one call for large directories
 */
function readDirectoryEntries(dirEntry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const reader = dirEntry.createReader();
    const allEntries: FileSystemEntry[] = [];

    // readEntries may need to be called multiple times for large directories
    const readBatch = () => {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            // No more entries
            resolve(allEntries);
          } else {
            allEntries.push(...entries);
            // Continue reading (there may be more)
            readBatch();
          }
        },
        reject
      );
    };

    readBatch();
  });
}

/**
 * Check if the browser supports folder upload via drag/drop
 */
export function supportsFolderDrop(): boolean {
  // Check for webkitGetAsEntry support
  if (typeof DataTransferItem === 'undefined') {
    return false;
  }

  // webkitGetAsEntry is available in all modern browsers
  return 'webkitGetAsEntry' in DataTransferItem.prototype;
}

/**
 * Check if the browser supports folder selection via file input
 */
export function supportsFolderPicker(): boolean {
  // webkitdirectory is supported in all modern browsers
  const input = document.createElement('input');
  return 'webkitdirectory' in input;
}
